import React, { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';

import { Alert, Button, Card, CardHeader, CardBody, CardFooter, Form, Input, InputGroupText, InputGroup, FormGroup, Label, Row, Col } from 'reactstrap';
import toast from 'react-hot-toast';

import { frontend } from '../../../api';
import { validateDate, validateEmailAddress } from '../../../validator';
import { FaMailBulk, FaRegCalendarAlt, FaSpinner, FaUser } from 'react-icons/fa';
import { FailedToFetchException } from 'api/exceptions/FailedToFetchException';

interface Props {
	onVerifyStepClick: (data: { bsaId?: string; dob?: string; loginToken?: string; verificationCodeEmail?: string }) => void;
}

export const GuestLogin = (props: Props) => {
	const { onVerifyStepClick } = props;
	const [checkButtonEnabled, setCheckButtonEnabled] = useState(false);
	const [showValidationMessage, setShowValidationMessage] = useState<boolean>(false);
	const [validationMessage, setValidationMessage] = useState<string | null>(null);
	const [isSubmitInProgress, setIsSubmitInProgress] = useState(false);
	const [messageState, setMessageState] = useState('info');
	const [message, setMessage] = useState(null);
	const [data, setData] = useState<{
		firstName?: string;
		lastName?: string;
		dob?: string;
		email?: string;
		loginToken?: string;
		verificationCodeEmail?: string;
	}>({});
	const [loginMessage, setLoginMessage] = useState<string | null>(null);

	useEffect(() => {
		const getContents = async () => {
			try {
				const result = await frontend.getMessage();

				if (result != null) {
					setLoginMessage(result);
				}
			} catch (error) {
				if (error instanceof FailedToFetchException) {
					toast.error(error.message);
				} else {
					Sentry.captureException(error);
				}
			}
		};

		getContents();
	}, []);

	useEffect(() => {
		let canSave = true;
		let validationMsg: string | null = null;

		if (data.firstName == null || data.firstName === '') {
			canSave = false;
		} else if (data.lastName == null || data.lastName === '') {
			canSave = false;
		} else if (data.dob == null || data.dob === '') {
			canSave = false;
		} else if (!validateDate(data.dob!)) {
			canSave = false;
		} else if (data.email == null || data.email === '') {
			canSave = false;
		} else if (!validateEmailAddress(data.email!)) {
			canSave = false;
		} else if (canSave) {
			const currentDate = new Date();
			currentDate.setUTCHours(0, 0, 0, 0);
			currentDate.setUTCDate(currentDate.getUTCDate() - 1);

			const oldDate = new Date(1900, 0, 1, 0, 0, 0, 0);

			const dobObj = new Date(data.dob!);
			if (dobObj.getTime() >= currentDate.getTime() || dobObj.getTime() < oldDate.getTime()) {
				canSave = false;
				validationMsg = 'Invalid date entered';
			}
		}

		setCheckButtonEnabled(canSave);
		setValidationMessage(validationMsg);
	}, [data]);

	const handleInputFieldChange = (fieldName: 'firstName' | 'lastName' | 'dob' | 'email', newValue: any) => {
		const newData = { ...data };
		newData[fieldName] = newValue;
		setData(newData);
		setShowValidationMessage(true);
	};

	const handleCheckClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		setMessageState('info');
		setMessage(null);
		setIsSubmitInProgress(true);

		try {
			const result = await frontend.guestLogin(data.email!, data.dob!, data.firstName!, data.lastName!);
			const newData = { ...data };
			newData.loginToken = result.token;
			newData.verificationCodeEmail = result.verificationCodeEmail;
			onVerifyStepClick(newData);
		} catch (error: any) {
			setMessageState('danger');
			setMessage(error.message);
			setIsSubmitInProgress(false);
		}
	};

	return (
		<Form action="" className="form" method="" onSubmit={(e) => handleCheckClick(e)}>
			<Card className="card-login">
				<CardHeader>
					<CardHeader>
						<h3 className="header text-center">PA Clearances Portal</h3>
						<h5 className="header text-center">New Member Submission</h5>
					</CardHeader>
				</CardHeader>
				<CardBody>
					<Row>
						<Col sm="6">
							<FormGroup>
								<Label for="bsaId">First Name</Label>
								<InputGroup>
									<InputGroupText style={{ background: 'none' }}>
										<FaUser color="#66615b" />
									</InputGroupText>
									<Input type="text" placeholder="Enter First Name" onChange={(e) => handleInputFieldChange('firstName', e.target.value)} />
								</InputGroup>
							</FormGroup>
						</Col>
						<Col sm="6">
							<FormGroup>
								<Label for="dob">Last Name</Label>
								<InputGroup>
									<InputGroupText style={{ background: 'none' }}>
										<FaRegCalendarAlt color="#66615b" />
									</InputGroupText>
									<Input type="text" placeholder="Enter Last Name" onChange={(e) => handleInputFieldChange('lastName', e.target.value)} />
								</InputGroup>
							</FormGroup>
						</Col>
					</Row>
					<Row>
						<Col sm="6">
							<FormGroup>
								<Label for="dob">Date of Birth</Label>
								<InputGroup>
									<InputGroupText style={{ background: 'none' }}>
										<FaRegCalendarAlt color="#66615b" />
									</InputGroupText>
									<Input type="date" placeholder="Enter date of birth" onChange={(e) => handleInputFieldChange('dob', e.target.value)} />
								</InputGroup>
							</FormGroup>
						</Col>

						<Col sm="6">
							<FormGroup>
								<Label for="dob">Email Address</Label>
								<InputGroup>
									<InputGroupText style={{ background: 'none' }}>
										<FaMailBulk color="#66615b" />
									</InputGroupText>
									<Input type="text" placeholder="Enter email address" onChange={(e) => handleInputFieldChange('email', e.target.value)} />
								</InputGroup>
							</FormGroup>
						</Col>
					</Row>
				</CardBody>
				<CardFooter>
					<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
						<div style={{ width: '100%' }}>
							<Button
								block
								className="btn-round mb-3"
								color="primary"
								type="button"
								disabled={isSubmitInProgress || (!isSubmitInProgress && !checkButtonEnabled)}
								onClick={(e) => handleCheckClick(e)}
							>
								{isSubmitInProgress ? <FaSpinner size={12} className="fa-spin" /> : 'Continue'}
							</Button>
						</div>
						<div>
							<a href="/app/login" style={{ color: '#000' }}>
								Have a BSA Member ID? Click here
							</a>
						</div>
					</div>
				</CardFooter>
			</Card>
		</Form>
	);
};
